import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../components/Header'
import InfoTooltip from '../components/InfoTooltip'
import Sidebar from '../components/Sidebar'
import HikvisionDeviceDashboardSkeleton from '../components/dashboard/HikvisionDeviceDashboardSkeleton'
import HikvisionLiveVideo from '../components/dashboard/HikvisionLiveVideo'
import DeviceAvailabilityAnalytics from '../components/dashboard/DeviceAvailabilityAnalytics'
import DeviceOfflineBanner from '../components/dashboard/DeviceOfflineBanner'
import DevicePageTop from '../components/dashboard/DevicePageTop'
import HikvisionDeviceParams from '../components/dashboard/HikvisionDeviceParams'
import { EditButtons, MetaField } from '../components/dashboard/MetaField'
import { NavPageLink, schoolSurface } from '../components/dashboard/schoolEquipmentChrome'
import {
  checkDeviceReachability,
  deleteDoor,
  fetchDoorOverview,
  updateDoor,
} from '../api/hikvision'
import { fetchAlerts } from '../api/dashboard'
import { fetchPanicIncidents } from '../api/panic'
import { formatDuration, formatStampAlmaty } from '../lib/formatDuration'
import { pickPanicDownSince } from '../lib/deviceDownSince'

const isAdminOrEngineer = (role) => {
  const r = typeof role === 'string' ? role.trim().toLowerCase() : ''
  return r === 'admin' || r === 'engineer'
}

const IncidentStatusPill = ({ status, t }) => {
  const styles = {
    open: 'text-critical',
    accepted: 'text-warning',
    closed: 'text-muted-400',
  }
  return (
    <span className={`text-xs font-medium ${styles[status] || styles.closed}`}>
      {t(`panic.status.${status}`, status)}
    </span>
  )
}

const PanicStationDashboard = ({ setIsAuthenticated, onLogout, userRole }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ip, deviceId } = useParams()

  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const [overview, setOverview] = useState(null)
  const [incidents, setIncidents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showDetails, setShowDetails] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [seriesRefreshKey, setSeriesRefreshKey] = useState(0)
  const [downAlerts, setDownAlerts] = useState([])

  const [editingField, setEditingField] = useState(null)
  const [draft, setDraft] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  const [checkResult, setCheckResult] = useState(null)

  const canManage = isAdminOrEngineer(userRole)

  const fetchData = async ({ silent = false } = {}) => {
    if (!deviceId) return
    if (silent) setRefreshing(true)
    else setIsLoading(true)
    setError(null)
    try {
      const [overviewData, incidentsRes, alertsData] = await Promise.all([
        fetchDoorOverview(deviceId),
        fetchPanicIncidents({ device_id: deviceId, limit: 20 }).catch(() => ({ data: [] })),
        fetchAlerts().catch(() => []),
      ])

      if (overviewData?.device?.device_type && overviewData.device.device_type !== 'panic') {
        const type = overviewData.device.device_type
        if (type === 'nvr') {
          navigate(`/schools/${encodeURIComponent(ip)}/nvr/${deviceId}`, { replace: true })
        } else {
          navigate(`/schools/${encodeURIComponent(ip)}/doors/${deviceId}`, { replace: true })
        }
        return
      }

      setOverview(overviewData)
      setIncidents(Array.isArray(incidentsRes?.data) ? incidentsRes.data : [])
      setDownAlerts(Array.isArray(alertsData) ? alertsData : [])
      setEditingField(null)
      if (silent) setSeriesRefreshKey((key) => key + 1)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('common.error'))
    } finally {
      setIsLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [deviceId])

  const handleSave = async (body) => {
    setIsSaving(true)
    setSaveError(null)
    try {
      await updateDoor(deviceId, body)
      setEditingField(null)
      setDraft({})
      fetchData({ silent: true })
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || t('common.error'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(t('schoolDashboard.panicDeleteConfirm'))) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteDoor(deviceId)
      navigate(`/schools/${encodeURIComponent(ip)}`)
    } catch (err) {
      setDeleteError(err?.response?.data?.message || t('common.error'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCheckReachability = async () => {
    if (!device?.host) {
      setCheckResult({ reachable: false, message: t('schoolDashboard.hikCheckHostRequired') })
      return
    }
    setIsChecking(true)
    setCheckResult(null)
    setDeleteError(null)
    try {
      const data = await checkDeviceReachability({
        id: Number(deviceId),
        host: device.host,
        port: device.port,
        use_https: !!device.use_https,
        device_type: device.device_type || 'panic',
      })
      setCheckResult(data)
    } catch (err) {
      setCheckResult({
        reachable: false,
        message: err?.response?.data?.message || err?.message || t('schoolDashboard.hikCheckFailed'),
      })
    } finally {
      setIsChecking(false)
    }
  }

  const startEdit = (field, value) => {
    setSaveError(null)
    setEditingField(field)
    setDraft(value)
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setDraft({})
    setSaveError(null)
  }

  const device = overview?.device
  const metrics = overview?.metrics
  const source = overview?.source
  const upValue = metrics?.up === null || metrics?.up === undefined ? null : Number(metrics.up)
  const isUp = upValue === 1
  const isDown = upValue === 0
  const address =
    device?.host && device?.port != null ? `${device.host}:${device.port}` : device?.target
  const downSince = pickPanicDownSince(downAlerts, {
    deviceId,
    target: address || device?.target,
  })

  const sortedZones = useMemo(() => {
    const zones = metrics?.zones || []
    return [...zones].sort((a, b) => Number(a.zone_id) - Number(b.zone_id))
  }, [metrics?.zones])

  const info = metrics?.device_info || {}
  const model = device?.model || info.model
  const firmware = device?.firmware || info.firmware
  const serial = device?.serial || info.serial
  const downDuration = formatDuration(downSince)
  const downStamp = formatStampAlmaty(downSince)

  const streamConnected = Number(metrics?.panic_stream_connected) === 1
  const zoneMisconfigured = Number(metrics?.panic_zone_misconfigured) === 1
  const zoneNotReset = Number(metrics?.panic_zone_not_reset) === 1
  const talkMisconfigured = Number(metrics?.panic_talk_misconfigured) === 1
  const zoneAlarm = sortedZones.some((zone) => Number(zone.alarm) === 1)

  const stats = [
    {
      label: t('panic.stream'),
      value: isUp ? (streamConnected ? t('common.online') : t('common.offline')) : null,
      tone: isUp ? (streamConnected ? 'text-positive' : 'text-critical') : undefined,
    },
    { label: t('panic.zones'), value: isUp ? sortedZones.length || null : null },
    {
      label: t('panic.zoneStatus'),
      value: isUp
        ? zoneAlarm
          ? t('panic.zoneAlarm')
          : t('panic.zoneOk')
        : null,
      tone: isUp ? (zoneAlarm ? 'text-critical' : 'text-positive') : undefined,
    },
  ]

  const chartEmptyMessage = useMemo(() => {
    if (source && source.live === false && source.prometheus === false) {
      return t('schoolDashboard.panicDashboardListenerOffline')
    }
    if (source && source.prometheus === false) {
      return t('schoolDashboard.panicDashboardPromPending')
    }
    return t('schoolDashboard.panicDashboardNoChartData')
  }, [source, t])

  const goToSchool = () => navigate(`/schools/${encodeURIComponent(ip)}`)
  const refresh = () => fetchData({ silent: true })

  const analyticsSection = (
    <DeviceAvailabilityAnalytics
      t={t}
      deviceId={deviceId}
      currentUp={upValue}
      downSince={downSince}
      refreshKey={seriesRefreshKey}
      modes={[{ id: 'availability', label: t('nvr.chartAvailability'), title: t('nvr.availability') }]}
      emptyMessage={chartEmptyMessage}
    />
  )

  const extraFields = (
    <MetaField
      label={t('door.videoChannel')}
      editing={editingField === 'channel'}
      canEdit={canManage}
      onStartEdit={() => startEdit('channel', { video_channel: device.video_channel || '101' })}
      editTitle={t('common.edit')}
      mono
      editor={
        <>
          <select
            value={draft.video_channel ?? '101'}
            onChange={(e) => setDraft((d) => ({ ...d, video_channel: e.target.value }))}
            className="min-w-[140px] rounded-lg bg-surface-2 px-2 py-1 text-sm text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            disabled={isSaving}
          >
            <option value="101">101 (main)</option>
            <option value="102">102 (sub)</option>
          </select>
          <EditButtons
            onSave={() => handleSave({ video_channel: String(draft.video_channel || '101') })}
            onCancel={handleCancelEdit}
            updateLoading={isSaving}
          />
        </>
      }
    >
      {device?.video_channel || '101'}
    </MetaField>
  )

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="absolute inset-0 -z-10 bg-brand-radial" />
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((prev) => !prev)}
        onLogout={onLogout}
        userEmail={localStorage.getItem('userEmail') || ''}
        userRole={userRole}
        setIsAuthenticated={setIsAuthenticated}
      />
      <div className="flex flex-1">
        {isSidebarVisible && (
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            onLogout={onLogout}
            userRole={userRole}
            onClose={() => setSidebarVisible(false)}
          />
        )}
        <main className="relative flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-4 py-6 transition-all duration-300 ease-brand sm:px-6 lg:px-10">
          <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-brand-linear/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-64 bg-brand-radial blur-3xl" />

          {isLoading ? (
            <HikvisionDeviceDashboardSkeleton />
          ) : error ? (
            <div className="rounded-3xl bg-critical/10 px-6 py-8 text-sm text-critical">{error}</div>
          ) : !device ? (
            <div className="rounded-3xl bg-surface-1 px-6 py-12 text-center text-sm text-muted-300">
              {t('common.notFound', 'Устройство не найдено')}
            </div>
          ) : (
            <>
              <DevicePageTop
                crumbLabel={device.school || t('schoolDashboard.backToList')}
                onBack={goToSchool}
                kind={t('equipment.typeSingular.panic')}
                title={device.name}
                isUp={isUp}
                isDown={isDown}
                downDuration={downDuration}
                address={address}
                model={model}
                firmware={firmware}
                showDetails={showDetails}
                onToggleDetails={() => setShowDetails((v) => !v)}
                onRefresh={refresh}
                refreshing={refreshing}
                t={t}
              />

              {showDetails && (
                <HikvisionDeviceParams
                  device={device}
                  metrics={metrics}
                  serial={serial}
                  canManage={canManage}
                  t={t}
                  editingField={editingField}
                  draft={draft}
                  setDraft={setDraft}
                  startEdit={startEdit}
                  onCancelEdit={handleCancelEdit}
                  onSave={handleSave}
                  isSaving={isSaving}
                  saveError={saveError}
                  extraFields={extraFields}
                  onCheckReachability={handleCheckReachability}
                  isChecking={isChecking}
                  checkResult={checkResult}
                  onDelete={handleDelete}
                  isDeleting={isDeleting}
                  deleteError={deleteError}
                />
              )}

              {isDown && (
                <DeviceOfflineBanner
                  downStamp={downStamp}
                  noConnectionKey="panic.noConnection"
                  noConnectionSinceKey="panic.noConnectionSince"
                  hintKey="panic.offlineHint"
                  onRetry={refresh}
                  t={t}
                />
              )}

              <section className="grid grid-cols-3 gap-2">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-2xl bg-white/[0.03] px-4 py-3">
                    <p className="text-xs text-muted-500">{s.label}</p>
                    <p
                      className={`mt-1 font-display text-2xl tabular-nums ${
                        s.value == null ? 'text-muted-600' : s.tone || 'text-white'
                      }`}
                    >
                      {s.value ?? '—'}
                    </p>
                  </div>
                ))}
              </section>

              {!isUp && analyticsSection}

              {isUp ? (
                <section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                  <div className="flex flex-col gap-4 xl:col-span-4">
                    <div className={`w-full rounded-2xl p-3 ${schoolSurface.section}`}>
                      <header className="mb-2">
                        <h2 className="font-display text-base text-white">
                          {t('panic.zones')}{' '}
                          <span className="text-muted-500">{sortedZones.length || '—'}</span>
                        </h2>
                      </header>

                      {(zoneMisconfigured || zoneNotReset || talkMisconfigured) && (
                        <div className="mb-2 space-y-1.5">
                          {zoneMisconfigured && (
                            <p className="rounded-lg px-2.5 py-1.5 text-[11px] text-warning">
                              {t('schoolDashboard.panicDashboardMisconfigured')}
                            </p>
                          )}
                          {zoneNotReset && (
                            <p className="rounded-lg px-2.5 py-1.5 text-[11px] text-critical">
                              {t('schoolDashboard.panicDashboardZoneNotReset')}
                            </p>
                          )}
                          {talkMisconfigured && (
                            <p className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] text-warning">
                              <span>{t('schoolDashboard.panicDashboardTalkMisconfigured')}</span>
                              <InfoTooltip
                                text={t('schoolDashboard.panicDashboardTalkMisconfiguredHint')}
                                position="bottom"
                                width={240}
                              >
                                <span
                                  className="material-icons cursor-help text-[14px] leading-none opacity-80"
                                  aria-label={t('schoolDashboard.panicDashboardTalkMisconfiguredHint')}
                                >
                                  info
                                </span>
                              </InfoTooltip>
                            </p>
                          )}
                        </div>
                      )}

                      {sortedZones.length === 0 ? (
                        <div className="flex items-center rounded-xl bg-surface-2 px-2.5 py-3 text-sm text-muted-500">
                          {t('schoolDashboard.panicCardMonitoringHint')}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {sortedZones.map((zone) => {
                            const isAlarm = Number(zone.alarm) === 1
                            return (
                              <div key={zone.zone_id} className={`rounded-lg px-2.5 py-1.5 ${schoolSurface.card}`}>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <div className="min-w-[2.75rem]">
                                    <p className="text-[10px] text-muted-500">{t('panic.zone')}</p>
                                    <p className="font-display text-sm text-white">№ {zone.zone_id}</p>
                                  </div>
                                  <span className={`text-xs font-medium ${isAlarm ? 'text-critical' : 'text-positive'}`}>
                                    {isAlarm ? t('panic.zoneAlarm') : t('panic.zoneOk')}
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    <div className={`w-full rounded-2xl p-3 ${schoolSurface.section}`}>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h2 className="font-display text-base text-white">{t('panic.incidents')}</h2>
                        <NavPageLink icon="notifications_active" onClick={() => navigate('/panic-incidents')}>
                          {t('navigation.panic')}
                        </NavPageLink>
                      </div>
                      {incidents.length === 0 ? (
                        <p className="px-0.5 text-sm text-muted-400">{t('panic.noIncidents')}</p>
                      ) : (
                        <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto pr-1">
                          {incidents.map((inc) => (
                            <li
                              key={inc.id}
                              className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 ${schoolSurface.card}`}
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm tabular-nums text-muted-200">
                                  {formatStampAlmaty(inc.last_trigger_at || inc.created_at) || '—'}
                                </p>
                                {inc.trigger_source && (
                                  <p className="truncate text-xs text-muted-400">{inc.trigger_source}</p>
                                )}
                              </div>
                              <IncidentStatusPill status={inc.status} t={t} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="xl:col-span-8">
                    <div className={`rounded-3xl p-6 ${schoolSurface.section}`}>
                      <header className="mb-4">
                        <h2 className="font-display text-lg text-white">{t('panic.video')}</h2>
                      </header>
                      <div className="aspect-video overflow-hidden rounded-2xl bg-surface-2">
                        {device.video_enabled !== false ? (
                          <HikvisionLiveVideo
                            key={`${device.id}-${device.video_channel || '101'}`}
                            deviceId={device.id}
                            videoEnabled
                            defaultChannel={device.video_channel || '101'}
                            className="h-full"
                          />
                        ) : (
                          <div className="flex h-full min-h-[16rem] items-center justify-center text-sm text-muted-500">
                            {t('door.videoDisabled')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="flex items-center gap-3 rounded-3xl bg-surface-1 px-6 py-5 text-sm text-muted-400">
                  <span className="material-icons text-muted-600">videocam_off</span>
                  {t('panic.zonesUnavailable')}
                </section>
              )}

              {isUp && analyticsSection}
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default PanicStationDashboard
